/**
 * CheckoutPage — FAZ-2: Stripe EMBEDDED Checkout (on-page) + premium wrapper + tek-kaynak (/api/packages) + English-only.
 *
 * Akis: /api/packages -> paket-ozeti (SOL) ; POST /api/payments/create-checkout {ui_mode:'embedded'} -> client_secret
 *        -> <EmbeddedCheckoutProvider><EmbeddedCheckout/></> (SAG, opara.co'da kalir; redirect YOK).
 * Para-mantik backend'de (mode-routing/subscription/idempotency); bu sayfa yalniz skin+entegrasyon.
 */
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, AlertCircle, ShieldCheck, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";

import { API } from "@/utils/apiConfig";
import { getLoginPath } from "@/utils/localePaths";
import { getStripePromise } from "@/utils/stripeClient";

const GOLD = "#F5C518";

export default function CheckoutPage() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const packageId = searchParams.get("package");

  const [pkg, setPkg] = useState(null);          // /api/packages'ten aktif paket
  const [pkgLoading, setPkgLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState(null);
  const [starting, setStarting] = useState(false);
  const [stripePromise] = useState(() => getStripePromise());

  const loginPath = getLoginPath ? getLoginPath() : "/en/login";
  const pricingPath = "/en/pricing";

  // Auth guard
  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate(loginPath);
  }, [user, navigate, loginPath, authLoading]);

  // FAZ-2 TEK-KAYNAK: paketler /api/packages'ten (frontend PACKAGES const KALDIRILDI). Tek-sefer fetch, t yok.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/packages`);
        const list = res?.data?.packages || [];
        const found = list.find((p) => p.package_id === packageId);
        if (active) setPkg(found || null);
      } catch (e) {
        if (active) setPkg(null);
      } finally {
        if (active) setPkgLoading(false);
      }
    })();
    return () => { active = false; };
  }, [packageId]);

  const startEmbedded = async () => {
    if (!token || !pkg) return;
    setStarting(true);
    try {
      const res = await axios.post(
        `${API}/payments/create-checkout`,
        {
          package_id: pkg.package_id,
          origin_url: window.location.origin,
          locale: "en",
          ui_mode: "embedded",
          subscription_approval: pkg.mode === "subscription",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // COMMIT-4: Pro->Business UPGRADE backend'de in-place modify ile yapilir -> client_secret DONMEZ,
      //   {upgraded:true} doner. Stripe frame acma; basari mesaji + profile'e don.
      if (res.data?.upgraded) {
        toast.success("Your plan has been upgraded.");
        navigate("/en/settings");
        return;
      }
      if (res.data?.client_secret) {
        setClientSecret(res.data.client_secret);
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (error) {
      const status = error.response?.status;
      // Hata detail'i {code,message} obje olabilir (i18n-code'lu 403'ler: already_active / downgrade /
      //   not_found_contact_support). Objeyi ham gosterme -> .message al.
      const _d = error.response?.data?.detail;
      const _msg = (_d && typeof _d === "object") ? (_d.message || "Request failed.") : (_d || null);
      if (status === 403) toast.error(_msg || "This action is not allowed.");
      else if (status === 409) toast.error(_msg || "We could not locate your subscription. Please contact support.");
      else if (status === 400) toast.error(_msg || "Invalid request.");
      else if (status === 503) toast.error("Payment system is warming up. Please try again in a moment.");
      else toast.error(_msg || "Could not start checkout.");
    } finally {
      setStarting(false);
    }
  };

  // Loading / invalid package
  if (pkgLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#0B0B0D" }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: GOLD }} />
      </div>
    );
  }
  if (!pkg) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4" style={{ backgroundColor: "#0B0B0D" }}>
        <AlertCircle className="w-12 h-12 text-red-400" />
        <p className="text-gray-300">Package not found.</p>
        <Button onClick={() => navigate(pricingPath)} className="text-black font-bold" style={{ backgroundColor: GOLD }}>
          View Plans
        </Button>
      </div>
    );
  }

  const isSub = pkg.mode === "subscription";
  const priceLabel = `$${Number(pkg.price_usd).toLocaleString("en-US")}`;

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: "#0B0B0D" }}>
      {/* Top bar: logo + back */}
      <header className="border-b border-gray-800/80">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/en/")}>
            <img src="/opara-logo-trimmed.png" alt="OPARA" className="h-8 w-auto object-contain" />
          </div>
          <button onClick={() => navigate(pricingPath)} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Plans
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 lg:py-12 grid grid-cols-1 lg:grid-cols-[minmax(0,420px)_1fr] gap-6 lg:gap-10 items-start">
        {/* SOL: Order summary (gold-border) */}
        <aside className="rounded-2xl border p-6" style={{ borderColor: `${GOLD}55`, background: "linear-gradient(180deg,#141416,#0E0E10)" }}>
          <h1 className="text-xl font-bold mb-1">Order Summary</h1>
          <p className="text-sm text-gray-400 mb-5">Review your plan before payment.</p>

          <div className="flex items-baseline justify-between border-b border-gray-800 pb-4 mb-4">
            <div>
              <div className="text-lg font-bold">{pkg.name}</div>
              <div className="text-xs text-gray-500">{isSub ? "Monthly subscription" : "One-time purchase"}</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-extrabold" style={{ color: GOLD }}>{priceLabel}</div>
              <div className="text-[11px] text-gray-500">{isSub ? "/ month" : "USD"}</div>
            </div>
          </div>

          <ul className="space-y-2 mb-5">
            {(pkg.features_en || []).map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between rounded-lg bg-black/40 border border-gray-800 px-4 py-3 mb-2">
            <span className="text-sm text-gray-400">Billed today</span>
            <span className="text-lg font-bold">{priceLabel}</span>
          </div>
          {isSub && (
            <p className="text-[11px] text-gray-500">Auto-renews monthly until cancelled. Cancel anytime from your account.</p>
          )}
        </aside>

        {/* SAG: Embedded frame (veya CTA) */}
        <section className="min-w-0">
          {!clientSecret ? (
            <div className="rounded-2xl border border-gray-800 bg-[#141416] p-6 lg:p-8">
              <h2 className="text-lg font-bold mb-4">Secure Checkout</h2>
              <Button onClick={startEmbedded} disabled={starting} className="w-full h-12 text-black font-bold text-base" style={{ backgroundColor: GOLD }}>
                {starting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Lock className="w-5 h-5 mr-2" />}
                {starting ? "Preparing…" : `Pay ${priceLabel}`}
              </Button>
              <p className="text-[11px] text-gray-500 mt-3">
                By continuing you agree to our{" "}
                <Link to="/en/terms" className="underline hover:text-white">Terms of Service</Link> and{" "}
                <Link to="/en/privacy" className="underline hover:text-white">Privacy Policy</Link>.
              </p>
            </div>
          ) : (
            <div className="rounded-2xl border border-gray-800 bg-white p-2 sm:p-4 overflow-hidden">
              {/* Stripe Embedded Checkout (Dashboard-Branding: gold/logo/font) */}
              <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}

          {/* Trust row */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 text-[12px] text-gray-500">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" style={{ color: GOLD }} /> Secured by Stripe</span>
            <span className="flex items-center gap-1.5"><Lock className="w-4 h-4" style={{ color: GOLD }} /> 256-bit SSL</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" style={{ color: GOLD }} /> PCI-DSS compliant</span>
          </div>
        </section>
      </main>
    </div>
  );
}
